// Core packages
import { OnDestroy, OnInit } from "@angular/core";
// Third party packages
import { MatDialog, MatDialogRef } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { forkJoin, of } from "rxjs";
import { catchError, finalize, switchMap, take } from "rxjs/operators";
// Custom packages
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { HelperService } from "src/app/shared/services/helper.service";
import { CondoStructuresService } from "src/app/modules/properties/condo-structures.service";
import { PropertiesService } from "src/app/modules/properties/properties.service";
/**
 * Back-end hard limit (@see PrivacyDocumentService.MAX_PROPERTIES_PER_REQUEST):
 * a single generation request cannot target more than 50 properties.
 */
var MAX_PROPERTIES_PER_REQUEST = 50;
/**
 * Script start
 */
var GeneratePrivacyDocsDialogComponent = /** @class */ (function () {
    /**
     * Class constructor
     */
    function GeneratePrivacyDocsDialogComponent(dialogRef, data, translateService, toastrService, dialog, helperService, condoStructuresService, propertiesService) {
        this.dialogRef = dialogRef;
        this.data = data;
        this.translateService = translateService;
        this.toastrService = toastrService;
        this.dialog = dialog;
        this.helperService = helperService;
        this.condoStructuresService = condoStructuresService;
        this.propertiesService = propertiesService;
        this.properties = [];
        this.selectAll = false;
        this.maxProperties = MAX_PROPERTIES_PER_REQUEST;
        // "Close mapping" feature state
        this.selectAllCloseMapping = false;
        this.closeMappingInProgress = false;
        this.subscriptions = [];
    }
    /**
     * Init components
     *
     * @since 1.0.0
     */
    GeneratePrivacyDocsDialogComponent.prototype.ngOnInit = function () {
        var _this = this;
        var sourceProperties = (this.data && Array.isArray(this.data.properties)) ? this.data.properties : [];
        this.properties = sourceProperties
            .filter(function (item) { return item && item._id; })
            .map(function (item) { return ({
            id: item._id,
            name: item.name || "",
            addressLabel: _this.buildAddressLabel(item.address),
            value: false,
            structureId: item.structure || "",
            mappingOpen: item.structureOpen === true || item.structureOpen === "true",
            closeMappingSelected: false,
        }); });
    };
    /**
     * Unsubscribe from every pending subscription
     *
     * @since 1.0.0
     */
    GeneratePrivacyDocsDialogComponent.prototype.ngOnDestroy = function () {
        this.subscriptions.forEach(function (subscription) { return subscription.unsubscribe(); });
    };
    /**
     * Build a readable "route streetNumber, city" label from a CustomAddress-shaped object
     *
     * @since 1.0.0
     */
    GeneratePrivacyDocsDialogComponent.prototype.buildAddressLabel = function (address) {
        if (!address) {
            return "";
        }
        var streetPart = [address.route, address.streetNumber].filter(Boolean).join(" ");
        return [streetPart, address.city].filter(Boolean).join(", ");
    };
    Object.defineProperty(GeneratePrivacyDocsDialogComponent.prototype, "selectedCount", {
        /**
         * Number of properties currently selected
         *
         * @since 1.0.0
         */
        get: function () {
            return this.properties.filter(function (item) { return item.value; }).length;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GeneratePrivacyDocsDialogComponent.prototype, "openMappingProperties", {
        /**
         * Properties whose mapping ("mappatura") is currently open
         *
         * @since 1.0.0
         */
        get: function () {
            return this.properties.filter(function (item) { return item.mappingOpen; });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(GeneratePrivacyDocsDialogComponent.prototype, "closeMappingSelectedCount", {
        /**
         * Number of properties currently checked for the "close mapping" action
         *
         * @since 1.0.0
         */
        get: function () {
            return this.properties.filter(function (item) { return item.mappingOpen && item.closeMappingSelected; }).length;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Handle "Seleziona tutti" toggle. Properties with an open mapping are excluded
     * since they cannot be selected for privacy docs generation.
     *
     * @since 1.0.0
     */
    GeneratePrivacyDocsDialogComponent.prototype.onSelectAll = function (checked) {
        this.selectAll = checked;
        this.properties.forEach(function (item) {
            if (!item.mappingOpen) {
                item.value = checked;
            }
        });
    };
    /**
     * Handle "Seleziona tutte le mappature aperte" toggle
     *
     * @since 1.0.0
     */
    GeneratePrivacyDocsDialogComponent.prototype.onSelectAllCloseMapping = function (checked) {
        this.selectAllCloseMapping = checked;
        this.properties.forEach(function (item) {
            if (item.mappingOpen) {
                item.closeMappingSelected = checked;
            }
        });
    };
    /**
     * Resolve the CondoStructure ID for the given property. Uses the ID already carried
     * by the (lightweight) sub-properties list when available; otherwise falls back to
     * fetching the full property document, which always has the "structure" field
     * (@see PropertyDetailPage, where the same "structure" field is used to load the
     * CondoStructure for the mapping tab).
     *
     * @since 1.0.0
     */
    GeneratePrivacyDocsDialogComponent.prototype.resolveStructureId = function (item) {
        if (item.structureId) {
            return of(item.structureId);
        }
        return this.propertiesService.get(item.id).pipe(switchMap(function (res) { return of((res && res.data && res.data.structure) || ""); }), catchError(function () { return of(""); }));
    };
    /**
     * Handle "Chiudi la mappatura": call the close-mapping API for every selected
     * property with an open mapping (one request per CondoStructure, run in parallel).
     *
     * @since 1.0.0
     */
    GeneratePrivacyDocsDialogComponent.prototype.onCloseMapping = function () {
        var _this = this;
        var selected = this.properties.filter(function (item) { return item.mappingOpen && item.closeMappingSelected; });
        if (!selected.length) {
            return;
        }
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant("dialogs.confirm.title"),
                message: this.translateService.instant("companies.detail.privacyDocsCard.generatePropertiesDialog.confirmCloseMapping"),
                btnOkText: this.translateService.instant("common.cta.confirm"),
                btnCancelText: this.translateService.instant("common.cta.undo"),
            },
            width: "500px",
        });
        this.subscriptions.push(dialogRef.beforeClosed().pipe(take(1)).subscribe(function (confirmed) {
            if (!confirmed) {
                return;
            }
            _this.closeMappingInProgress = true;
            var requests = selected.map(function (item) {
                return _this.resolveStructureId(item).pipe(switchMap(function (structureId) {
                    if (!structureId) {
                        return of({
                            status: false,
                            message: _this.translateService.instant("companies.detail.privacyDocsCard.generatePropertiesDialog.closeMappingMissingStructureId", { name: item.name }),
                        });
                    }
                    return _this.condoStructuresService.update(structureId, { open: false }).pipe(catchError(function (err) { return of({
                        status: false,
                        message: (err && err.error && err.error.message) || null,
                    }); }));
                }));
            });
            _this.subscriptions.push(forkJoin(requests)
                .pipe(finalize(function () { _this.closeMappingInProgress = false; }))
                .subscribe(function (results) {
                var failed = results.filter(function (res) { return !res || !res.status; });
                var succeededItems = selected.filter(function (_, index) { return results[index] && results[index].status; });
                if (succeededItems.length) {
                    succeededItems.forEach(function (item) {
                        item.mappingOpen = false;
                        item.closeMappingSelected = false;
                    });
                    _this.selectAllCloseMapping = false;
                    _this.toastrService.success(_this.translateService.instant("companies.detail.privacyDocsCard.generatePropertiesDialog.closeMappingSuccess"));
                }
                if (failed.length) {
                    failed.forEach(function (res) {
                        _this.toastrService.error(res && res.message
                            ? res.message
                            : _this.translateService.instant("companies.detail.privacyDocsCard.generatePropertiesDialog.closeMappingError"));
                    });
                }
            }, function (err) {
                _this.helperService.handleError(err);
            }));
        }));
    };
    /**
     * Handle "Salva": close the dialog returning the selected property ids
     *
     * @since 1.0.0
     */
    GeneratePrivacyDocsDialogComponent.prototype.onSave = function () {
        var propertyIds = this.properties.filter(function (item) { return item.value; }).map(function (item) { return item.id; });
        if (!propertyIds.length) {
            return;
        }
        if (propertyIds.length > this.maxProperties) {
            this.toastrService.error(this.translateService.instant("companies.detail.privacyDocsCard.generatePropertiesDialog.maxPropertiesExceeded", { max: this.maxProperties }));
            return;
        }
        this.dialogRef.close({ propertyIds: propertyIds });
    };
    /**
     * Handle "Chiudi": close the dialog without generating anything
     *
     * @since 1.0.0
     */
    GeneratePrivacyDocsDialogComponent.prototype.onClose = function () {
        this.dialogRef.close();
    };
    return GeneratePrivacyDocsDialogComponent;
}());
export { GeneratePrivacyDocsDialogComponent };
